import { createApp, App as IAapp } from 'vue';
// 初始化多语言
import { setupI18n } from '@/plugins/vueI18n';
import App from './App.vue';
import router from './router';
import plugins from './plugins';
import store from './store/index';
import 'virtual:uno.css';
import 'virtual:svg-icons-register';
import useClipboard from '@/hooks/clipboard';
// 引入message组件样式
import 'element-plus/theme-chalk/src/message.scss';
import 'element-plus/theme-chalk/src/message-box.scss';
import { setupGlobCom } from './components';
import './responsive.ts';
import { trackPageView } from '@/utils/baiduAnalytics';
import directives from './directives';
import realtimePlugin from '@/plugins/realtime';
// 富文本列表样式（须在 reset 之后加载，覆盖 list-style: none）
import '@/style/rich-text-content.scss';
import { setupI18nInspector } from 'vite-plugin-tolgee-i18n/client';

// 检测华为浏览器并添加标识类
const detectHuaweiBrowser = () => {
    if (typeof window === 'undefined') return;

    const userAgent = navigator.userAgent.toLowerCase();
    // 检测华为浏览器：HuaweiBrowser、HUAWEI、华为浏览器等
    const isHuaweiBrowser = /huaweibrowser|huawei|华为浏览器/i.test(userAgent);

    if (isHuaweiBrowser) {
        // 立即添加类名
        document.documentElement.classList.add('huawei-browser');
        if (document.body) {
            document.body.classList.add('huawei-browser');
        }

        // 强制禁用字体自动调整 - JavaScript 层面（最高优先级，立即执行）
        const style = document.createElement('style');
        style.id = 'huawei-font-fix';
        style.textContent = `
            html, html.huawei-browser, 
            body, body.huawei-browser,
            :root, 
            * {
                -webkit-text-size-adjust: none !important;
                -moz-text-size-adjust: none !important;
                -ms-text-size-adjust: none !important;
                text-size-adjust: none !important;
            }
        `;
        // 插入到 head 的最前面，确保优先级最高
        if (document.head.firstChild) {
            document.head.insertBefore(style, document.head.firstChild);
        } else {
            document.head.appendChild(style);
        }

        // 动态引入华为补丁 CSS
        import('@/style/huawei-patch.scss');
    }
};

const setup = async () => {
    // 在应用启动前检测华为浏览器
    detectHuaweiBrowser();

    const app = createApp(App);
    app.use(router);
    app.use(directives);
    app.use(store);
    setupGlobCom(app);
    const i18n = await setupI18n(app);
    const clipboard = useClipboard(i18n.global.t);
    app.use(plugins);
    app.use(realtimePlugin, { autoConnect: true });
    const { mobile } = useStore('user');
    if (import.meta.env.VITE_MODE !== 'prod') {
        const tolgeeWhitelistMap: Record<string, string> = {
            '15001225236': 'tgpak_gm3f6zbqge2wgmzwgzuhandbm53g44dgorxg24trme4dqni',
            '15231105687': 'tgpak_gm3f6zdsmvydem3wgvsxgylgomzdsndvgnxgk5bzm4ydeoi',
        };
        const devKey = tolgeeWhitelistMap[mobile.value];
        if (devKey) {
            localStorage.setItem('tolgee-i18n-auth', devKey);
        }
        setupI18nInspector(app, i18n, {
            apiUrl: import.meta.env.VITE_APP_TOLGEE_API_URL || '',
            apiKey: import.meta.env.VITE_TOGGLE_SECRET || '',
            locale: 'zh-CN',
            theme: 'light',
            localeLoader: (lang: string) => import(`./locales/${lang}.ts`).then(m => m.default),
        });
    }

    app.mount('#app');
    clipboard.install();

    // 注意：初始页面访问由 router.afterEach 处理，这里不需要重复统计
};
setup();
